import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

export interface PublicNavLink {
  key: string;
  label: string;
  to?: string;
  onClick?: () => void;
  active?: boolean;
}

interface PublicNavProps {
  links: PublicNavLink[];
  onLogin: () => void;
  onContact: () => void;
}

/**
 * Shared header for public (unauthenticated) pages — the landing page and the
 * blog. Owns its own mobile drawer state so every consumer gets a working
 * hamburger on phones (previously only HomePage did; BlogPublicLayout's nav
 * links were unreachable below 768px).
 */
export default function PublicNav({ links, onLogin, onContact }: PublicNavProps) {
  const { isDark, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [mobileMenuOpen]);

  const closeMenu = () => setMobileMenuOpen(false);
  const handleLogin = () => { closeMenu(); onLogin(); };
  const handleContact = () => { closeMenu(); onContact(); };

  return (
    <header className="lp-nav" ref={menuRef}>
      <div className="lp-nav-inner">
        <Link to="/" className="lp-nav-brand" aria-label="tozenAI home" onClick={closeMenu}>
          <span className="lp-brand-tozen">tozen</span>
          <span className="lp-brand-labs">Labs</span>
        </Link>

        <nav
          className={`lp-nav-links${mobileMenuOpen ? " lp-nav-links--open" : ""}`}
          aria-label="Site navigation"
        >
          {links.map((link) =>
            link.to ? (
              <Link
                key={link.key}
                to={link.to}
                className={`lp-nav-link${link.active ? " blog-nav-active" : ""}`}
                onClick={closeMenu}
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.key}
                type="button"
                className="lp-nav-link"
                onClick={() => { closeMenu(); link.onClick?.(); }}
              >
                {link.label}
              </button>
            ),
          )}
          <button type="button" className="lp-nav-login lp-nav-login--mobile" onClick={handleLogin}>
            Sign in
          </button>
        </nav>

        <div className="lp-nav-actions">
          <button type="button" className="lp-theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {isDark ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
          <button type="button" className="lp-nav-login" onClick={handleLogin}>Sign in</button>
          <button type="button" className="lp-btn-primary lp-nav-cta" onClick={handleContact}>
            Contact us
          </button>
          <button
            type="button"
            className="lp-hamburger"
            aria-label="Toggle menu"
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            {mobileMenuOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
